import Vue from 'vue';
import _ from 'lodash';
import Router from 'vue-router';
import SignIn from '@/pages/SignIn';
import SignUp from '@/pages/SignUp';
import Invited from '@/pages/Invited';
import Request from '@/pages/Request';
import Reset from '@/pages/Reset';
import Confirm from '@/pages/Confirm';
import Resend from '@/pages/Resend';
import Activate from '@/pages/Activate';

// Home
import Home from '@/pages/Home/Home';
import Summary from '@/pages/Home/Summary';
import Dashboard from '@/pages/Home/Dashboard';
import Tests from '@/pages/Home/Tests';
import Profile from '@/pages/Home/Profile';
import Account from '@/pages/Home/Account';
import Test from '@/pages/Home/Test';
import TestSuites from '@/pages/Home/TestSuites';
import TestSuite from '@/pages/Home/TestSuite';
import TestRuns from '@/pages/Home/TestRuns';
import AMacros from '@/pages/Home/AMacros';
import AMacro from '@/pages/Home/AMacro';
import Results from '@/pages/Home/Results';
import LoadTests from '@/pages/Home/LoadTests';
import LoadTest from '@/pages/Home/LoadTest';
import LoadTestRunDetail from '@/pages/Home/LoadTestRunDetail';

// Home/SuperAdmin
import SuperAdminHome from '@/pages/Home/SuperAdmin/SuperAdminHome';
import SuperAdminDashboard from '@/pages/Home/SuperAdmin/SuperAdminDashboard';
import SuperAdminAccounts from '@/pages/Home/SuperAdmin/SuperAdminAccounts';
import SuperAdminAgents from '@/pages/Home/SuperAdmin/SuperAdminAgents';
import SuperAdminUsers from '@/pages/Home/SuperAdmin/SuperAdminUsers';
import SuperAdminUserEdit from '@/pages/Home/SuperAdmin/SuperAdminUserEdit';
import SuperAdminTests from '@/pages/Home/SuperAdmin/SuperAdminTests';
import SuperAdminTestSuites from '@/pages/Home/SuperAdmin/SuperAdminTestSuites';
import SuperAdminTransients from '@/pages/Home/SuperAdmin/SuperAdminTransients';
import SuperAdminTransient from '@/pages/Home/SuperAdmin/SuperAdminTransient';

// Integrations Pages
import User from '@/api/user';
import store from '@/store';
import InstallGitHub from './pages/Home/Integrations/InstallGitHub';
import InstallSlack from './pages/Home/Integrations/InstallSlack';
import IntegrationsHome from './pages/Home/Integrations/IntegrationsHome';
import Integrations from './pages/Home/Integrations/Integrations';

// Sample (for macro testing)
import Sample from './pages/Sample';

const requireAuth = (to, from, next) => User.isAuthenticated().then((authed) => {
  if (!authed) return next({path: '/signin', query: {redirect: to.fullPath}});
  return next();
});

const requireAccountAdminAuth = (to, from, next) => User.isAccountAdmin().then((authed) => {
  if (!authed) return next({path: '/signin', query: {redirect: to.fullPath}});
  return next();
});

const requireSuperAuth = (to, from, next) => User.isSuperAdmin().then((authed) => {
  if (!authed) return next({path: '/', query: {redirect: to.fullPath}});
  return next();
});

const getBreadcrumbs = (to) => {
  if (_.get(to, 'meta.breadcrumbs')) return to.meta.breadcrumbs;
  return null;
};

Vue.use(Router);
const router = new Router({
  mode: 'history',
  routes: [{
    path: '*', redirect: '/'
  }, {
    path: '/',
    name: 'Home',
    component: Home,
    beforeEnter: requireAuth,
    // Children will be rendered inside Apps's <router-view>
    children: [{
      path: 'profile',
      name: 'Profile',
      component: Profile,
      meta: {title: 'Profile', breadcrumbs: [{text: 'Profile'}]}
    }, {
      path: 'account',
      name: 'Account',
      beforeEnter: requireAccountAdminAuth,
      component: Account,
      meta: {title: 'Account', breadcrumbs: [{text: 'Account'}]}
    }, {
      path: '/dashboard',
      name: 'Dashboard',
      component: Dashboard,
      meta: {
        title: 'Dashboard',
        hasSubheader: true
      }
    }, {
      path: '/dashboard/:id(.*)',
      name: 'Dashboard',
      component: Dashboard,
      props: true,
      meta: {
        title: 'Dashboard',
        hasSubheader: true,
        dynamicBreadcrumb: true
      }
    }, {
      path: '/',
      name: 'Summary',
      component: Summary,
      meta: {
        title: 'Summary',
        hasSubheader: true
      }
    }, {
      path: 'tests',
      name: 'Tests',
      component: Tests,
      meta: {
        hasSubheader: true,
        breadcrumbs: [{text: 'Tests'}]
      }
    }, {
      path: 'testsuites',
      name: 'Suites',
      component: TestSuites,
      meta: {
        hasSubheader: true,
        breadcrumbs: [{text: 'Suites'}]
      }
    }, {
      path: 'results',
      name: 'Results',
      component: Results,
      meta: {
        breadcrumbs: [{text: 'Results'}]
      }
    }, {
      path: 'testsuite/:guid',
      name: 'Suite',
      component: TestSuite,
      props: true,
      meta: {
        title: 'Suite',
        dynamicBreadcrumb: true
      }
    }, {
      path: 'test/:guid',
      name: 'Test',
      component: Test,
      props: true,
      meta: {
        title: 'Test',
        dynamicBreadcrumb: true
      }
    }, {
      path: 'results/:runGuid',
      name: 'TestRuns',
      component: TestRuns,
      props: true,
      meta: {
        title: 'Result',
        dynamicBreadcrumb: true
      }
    }, {
      path: 'modules',
      name: 'AMacros',
      component: AMacros,
      meta: {
        hasSubheader: true,
        breadcrumbs: [{text: 'Modules'}]
      }
    }, {
      path: 'module/:guid',
      name: 'AMacro',
      component: AMacro,
      props: true,
      meta: {
        hasSubheader: true,
        dynamicBreadcrumb: true,
        title: 'Module'
      }
    }, {
      path: 'load-tests',
      name: 'LoadTests',
      component: LoadTests,
      meta: {
        hasSubheader: true,
        breadcrumbs: [{text: 'Load Tests'}]
      }
    }, {
      path: 'load-test/:guid',
      name: 'LoadTest',
      component: LoadTest,
      props: true,
      meta: {
        hasSubheader: true,
        dynamicBreadcrumb: true,
        title: 'Load Test'
      }
    }, {
      path: 'load-test/:guid/results/:runGuid',
      name: 'LoadTestRunDetail',
      component: LoadTestRunDetail,
      props: true,
      meta: {
        hasSubheader: true,
        dynamicBreadcrumb: true,
        title: 'Load Test Run Results'
      }
    }, {
      path: '/integrations',
      component: IntegrationsHome,
      children: [{
        path: '/',
        name: 'Integrations',
        component: Integrations,
        meta: {title: 'Integrations'}
      }, {
        path: 'github',
        name: 'GitHub',
        component: InstallGitHub,
        meta: {title: 'Installing GitHub App', bodyClass: 'front-background'}
      }, {
        path: 'slack',
        name: 'Slack',
        component: InstallSlack,
        meta: {title: 'Installing Slack App', bodyClass: 'front-background'}
      }]
    }, {
      path: '/super',
      component: SuperAdminHome,
      beforeEnter: requireSuperAuth,
      children: [{
        path: '/',
        name: 'Admin',
        component: SuperAdminDashboard,
        meta: {title: 'Admin Dashboard', breadcrumbs: [{text: 'Admin Dashboard'}]}
      }, {
        path: 'agents',
        name: 'AgentAdministration',
        component: SuperAdminAgents,
        meta: {
          title: 'Agent Administration',
          hasSubheader: true,
          breadcrumbs: [{text: 'Admin Dashboard', to: '/super', exact: true}, {text: 'Agents'}]
        }
      }, {
        path: 'accounts',
        name: 'AccountAdministration',
        component: SuperAdminAccounts,
        meta: {
          title: 'Account Administration',
          hasSubheader: true,
          breadcrumbs: [{text: 'Admin Dashboard', to: '/super', exact: true}, {text: 'Accounts'}]
        }
      }, {
        path: 'users',
        name: 'UserAdministration',
        component: SuperAdminUsers,
        meta: {
          title: 'User Administration',
          hasSubheader: true,
          breadcrumbs: [{text: 'Admin Dashboard', to: '/super', exact: true}, {text: 'Users'}]
        }
      }, {
        path: 'users/:id',
        name: 'UserDetailAdministration',
        props: true,
        component: SuperAdminUserEdit,
        meta: {
          title: 'User Details',
          hasSubheader: true,
          breadcrumbs: [
            {text: 'Admin Dashboard', to: '/super/dashboard'},
            {text: 'Users', to: '/super/users', exact: true},
            {text: 'User Details'}]
        }
      }, {
        path: 'tests',
        name: 'TestAdministration',
        component: SuperAdminTests,
        meta: {
          title: 'Test Administration',
          hasSubheader: true,
          breadcrumbs: [{text: 'Admin Dashboard', to: '/super', exact: true}, {text: 'Tests'}]
        }
      }, {
        path: 'suites',
        name: 'TestSuiteAdministration',
        component: SuperAdminTestSuites,
        meta: {
          title: 'Suite Administration',
          hasSubheader: true,
          breadcrumbs: [{text: 'Admin Dashboard', to: '/super', exact: true}, {text: 'Suites'}]
        }
      }, {
        path: 'transients',
        name: 'TransientAdministration',
        component: SuperAdminTransients,
        meta: {
          title: 'Transient Administration',
          hasSubheader: true,
          breadcrumbs: [{text: 'Admin Dashboard', to: '/super', exact: true}, {text: 'Transients'}]
        }
      }, {
        path: 'transients/:id',
        name: 'TransientDetailAdministration',
        props: true,
        component: SuperAdminTransient,
        meta: {
          title: 'Transient Detail',
          hasSubheader: true,
          breadcrumbs: [
            {text: 'Admin Dashboard', to: '/super/dashboard'},
            {text: 'Transients', to: '/super/transients', exact: true},
            {text: 'Transient Details'}]
        }
      }]
    }]
  }, {
    path: '/signin',
    component: SignIn,
    name: 'SignIn',
    meta: {bodyClass: 'front-background', title: 'Sign In'}
  }, {
    path: '/signup',
    component: SignUp,
    name: 'SignUp',
    meta: {bodyClass: 'front-background', title: 'Sign Up'}
  }, {
    path: '/confirm',
    component: Confirm,
    name: 'ConfirmEmail',
    meta: {bodyClass: 'front-background', title: 'Confirm Email'}
  }, {
    path: '/activate/:token',
    component: Activate,
    name: 'Activate',
    props: true,
    meta: {bodyClass: 'front-background', title: 'Activate'}
  }, {
    path: '/resend',
    component: Resend,
    name: 'ResendEmail',
    meta: {bodyClass: 'front-background', title: 'Resend Email'}
  }, {
    path: '/invited/:token',
    component: Invited,
    name: 'Invited',
    props: true,
    meta: {bodyClass: 'front-background', title: 'Invited User'}
  }, {
    path: '/request',
    component: Request,
    name: 'PasswordReset',
    meta: {bodyClass: 'front-background', title: 'Password Reset'}
  }, {
    path: '/reset/:token',
    component: Reset,
    name: 'AccountReset',
    props: true,
    meta: {bodyClass: 'front-background', title: 'Account Reset'}
  }, {
    path: '/sample',
    component: Sample,
    name: 'Sample',
    meta: {title: 'Bionic Metrics Sample'}
  }],

  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    return {x: 0, y: 0};
  }
});
router.beforeEach((to, from, next) => {
  if (!to.meta) return next();
  document.body.className = to.meta.bodyClass ? to.meta.bodyClass : '';
  document.title = to.meta.title ? to.meta.title : 'Bionic Metrics';
  store.commit('setTitle', to.meta.title);
  store.commit('setHasSubheader', !!to.meta.hasSubheader);
  store.commit('setBreadcrumb', getBreadcrumbs(to));
  store.commit('setSavingStatus', null);
  return next();
});

export default router;
